/** @file Activity panel: presence, notices, own batches being sent, the commit feed newest first. */
import { undoneRevs } from '@pose2d/api/queries/undoneRevs';
import type { IDockviewPanelProps } from 'dockview-react';
import { useMemo } from 'react';
import { useStore } from 'zustand';
import { feedPages } from '../../api/sync/feedPages';
import { useT } from '../../shell/i18n/useT';
import { liveStore } from '../../stores/liveStore';
import { Button } from '../../ui-kit/Button';
import { openAiView } from '../aiView/openAiView';
import { ActivityRow } from './ActivityRow';
import { NoticeRow } from './NoticeRow';
import { PresencePills } from './PresencePills';
import './activity.css';

/** The dock panel body (spec §9 Activity); an actor badge opens the AI view filtered to that actor. */
export function ActivityPanel({ containerApi }: IDockviewPanelProps) {
  const t = useT();
  const commits = useStore(liveStore, (s) => s.commits);
  const batches = useStore(liveStore, (s) => s.batches);
  const notices = useStore(liveStore, (s) => s.notices);
  const undone = useMemo(() => undoneRevs(commits), [commits]);
  const rows = useMemo(() => [...commits].reverse(), [commits]);
  const sending = batches.filter((b) => b.committedRev === null);
  const onActor = (actor: string): void => { openAiView(containerApi, actor); };
  return (
    <div className="activity" data-testid="panel-activity">
      <PresencePills />
      {notices.map((n) => <NoticeRow key={n.id} notice={n} />)}
      {sending.map((b) => <p key={b.seq} className="activity-pending">{t('activity.sending', { names: b.pending.map((p) => p.name).join(', ') })}</p>)}
      {rows.length === 0 ? <p className="activity-empty">{t('activity.empty')}</p> : null}
      <ol className="activity-feed">{rows.map((c) => <ActivityRow key={c.rev} summary={c} undone={undone.has(c.rev)} onActor={onActor} />)}</ol>
      {(commits[0]?.rev ?? 1) > 1 ? <Button label={t('activity.loadOlder')} onClick={() => { void feedPages.older(); }} /> : null}
    </div>
  );
}
